import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { CaretDownIcon } from "@phosphor-icons/react";
import { useAccessibility, useFilters, useI18N } from "common/hooks";
import type { FilterSectionResult } from "common/types";
import type { PlacesCityParams } from "common/types/places";
import {
  decodeSelection,
  encodeSelection,
  type SelectionKey,
} from "common/utils/accessibilitySelection";
import { BusinessTypeDropdown } from "@/components/Search/FilterPannel/BusinessType";
import { AccessibilityFilterPanel } from "@/components/Search/FilterPannel/Accessibility";
import {
  OpeningHoursFilterPanel,
  type OpeningHoursMode,
} from "@/components/Search/FilterPannel/OpeningHours";
import { hasPlacesFilters, placesSearch, samePlacesFilters } from "@/lib/placesPage";
import { cn } from "@/lib/utils";

type Panel = "types" | "accessibility" | "hours";

type PlacesFilterBarProps = {
  citySlug: string;
  search: PlacesCityParams;
  /** The city's own filter sections; absent when the facet failed. */
  sections?: FilterSectionResult[];
};

type Draft = {
  types: string[];
  accessibility: SelectionKey[];
  mode: OpeningHoursMode;
  day: string;
  time: string;
};

function draftFrom(search: PlacesCityParams): Draft {
  return {
    types: search.types ? search.types.split("|").filter(Boolean) : [],
    accessibility: decodeSelection(search.accessibilities, search.accessibility_sections),
    mode: search.openingHoursMode ?? "any",
    day: search.openingHoursDay ?? "",
    time: search.openingHoursTime ?? "",
  };
}

function paramsFrom(draft: Draft): PlacesCityParams {
  const { accessibilities, accessibility_sections } = encodeSelection(draft.accessibility);
  const custom = draft.mode === "custom" && draft.day !== "" && draft.time !== "";
  const params: PlacesCityParams = {
    types: draft.types.length > 0 ? draft.types.join("|") : undefined,
    accessibilities: accessibilities || undefined,
    accessibility_sections: accessibility_sections || undefined,
    openingHoursMode: draft.mode === "open_now" || custom ? draft.mode as "open_now" | "custom" : undefined,
    openingHoursDay: custom ? draft.day : undefined,
    openingHoursTime: custom ? draft.time : undefined,
  };
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined),
  ) as PlacesCityParams;
}

/**
 * The search page's filter panels for a city page. Each panel edits a draft while open; closing
 * it applies the draft to the URL, which reloads the city page from page 1.
 */
export function PlacesFilterBar({ citySlug, search, sections }: PlacesFilterBarProps) {
  const i18n = useI18N();
  const navigate = useNavigate();
  const { businessTypes } = useFilters();
  const { filterAccessibility } = useAccessibility();
  const [openPanel, setOpenPanel] = useState<Panel | null>(null);
  const [draft, setDraft] = useState<Draft>(() => draftFrom(search));
  const triggerRefs = useRef<Partial<Record<Panel, HTMLButtonElement | null>>>({});
  const returnFocusTo = useRef<Panel | null>(null);
  const wasOpenOnMouseDown = useRef<Panel | null>(null);

  useEffect(() => {
    setDraft(draftFrom(search));
  }, [search]);

  useEffect(() => {
    if (openPanel !== null || returnFocusTo.current === null) return;
    triggerRefs.current[returnFocusTo.current]?.focus();
    returnFocusTo.current = null;
  }, [openPanel]);

  // Takes the draft so a footer action can apply a change made in the same click, before state catches up.
  const applyDraft = (nextDraft: Draft) => {
    returnFocusTo.current = openPanel;
    setOpenPanel(null);
    const next = paramsFrom(nextDraft);
    if (!samePlacesFilters(next, search)) {
      void navigate({ to: `/places/${citySlug}`, search: placesSearch(next) });
    } else {
      // A half-set choice (custom hours without a time) applies nothing, so its badge must go too.
      setDraft(draftFrom(search));
    }
  };

  const closePanel = () => applyDraft(draft);

  const togglePanel = (panel: Panel) => {
    const wasOpen = wasOpenOnMouseDown.current === panel;
    wasOpenOnMouseDown.current = null;
    // The panel's outside-click handler already closed it on mousedown; reopening would undo that.
    if (wasOpen) return;
    if (openPanel === panel) {
      closePanel();
    } else {
      setOpenPanel(panel);
    }
  };

  const counts: Record<Panel, number> = {
    types: draft.types.length,
    accessibility: draft.accessibility.length,
    hours: draft.mode === "any" ? 0 : 1,
  };
  const labels: Record<Panel, string> = {
    types: i18n.get("businessType"),
    accessibility: i18n.get("accessibility"),
    hours: i18n.get("openingHours"),
  };

  const trigger = (panel: Panel) => (
    <button
      type="button"
      ref={(el) => {
        triggerRefs.current[panel] = el;
      }}
      onClick={() => togglePanel(panel)}
      onMouseDown={() => {
        wasOpenOnMouseDown.current = openPanel;
      }}
      aria-expanded={openPanel === panel}
      aria-controls={openPanel === panel ? `places-filter-${panel}` : undefined}
      aria-haspopup={panel === "types" ? undefined : "dialog"}
      className={cn(
        "flex min-h-[44px] items-center gap-2 rounded-full border border-border-token-brand-minimal px-4 focus-blue transition-colors",
        openPanel === panel ? "bg-container-brand-minimal" : "bg-bg-brand-secondary-default hover:bg-bg-brand-secondary-hover",
      )}
    >
      <span className="label-m-medium text-fg-neutral-strong whitespace-nowrap">{labels[panel]}</span>
      {counts[panel] > 0 ? (
        <>
          <span
            aria-hidden="true"
            className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-container-brand-subtle label-s-medium text-fg-neutral-onFilled"
          >
            {counts[panel]}
          </span>
          <span className="sr-only">{i18n.t("placesFilterSelected", { count: counts[panel] })}</span>
        </>
      ) : (
        <CaretDownIcon size={16} aria-hidden="true" focusable="false" className="text-fg-neutral-subtle" />
      )}
    </button>
  );

  return (
    <div role="group" aria-label={i18n.get("filters")} className="flex flex-wrap items-center gap-2">
      <div className="relative">
        {trigger("types")}
        {openPanel === "types" && (
          <div id="places-filter-types" className="absolute left-0 top-full z-1000 mt-2">
            <BusinessTypeDropdown
              isOpen={true}
              onClose={closePanel}
              selectedTypes={draft.types}
              onSelectionChange={(types) => setDraft((current) => ({ ...current, types }))}
              businessTypes={businessTypes}
            />
          </div>
        )}
      </div>

      <div className="relative">
        {trigger("accessibility")}
        {openPanel === "accessibility" && (
          <div id="places-filter-accessibility" className="absolute left-0 top-full z-1000 mt-2">
            <AccessibilityFilterPanel
              isOpen={true}
              onClose={closePanel}
              selectedOptions={draft.accessibility}
              onOptionsChange={(accessibility) => setDraft((current) => ({ ...current, accessibility }))}
              onClearAll={() => applyDraft({ ...draft, accessibility: [] })}
              onApply={closePanel}
              accessibilityTagsData={sections ?? (filterAccessibility.length > 0 ? filterAccessibility : undefined)}
            />
          </div>
        )}
      </div>

      <div className="relative">
        {trigger("hours")}
        {openPanel === "hours" && (
          <div id="places-filter-hours" className="absolute left-0 top-full z-1000 mt-2">
            <OpeningHoursFilterPanel
              isOpen={true}
              onClose={closePanel}
              selectedMode={draft.mode}
              onModeChange={(mode) => setDraft((current) => ({ ...current, mode }))}
              selectedDay={draft.day}
              onDayChange={(day) => setDraft((current) => ({ ...current, day }))}
              selectedTime={draft.time}
              onTimeChange={(time) => setDraft((current) => ({ ...current, time }))}
            />
          </div>
        )}
      </div>

      {hasPlacesFilters(search) && (
        <Link
          to={`/places/${citySlug}`}
          search={placesSearch({})}
          activeOptions={{ exact: true }}
          className="flex min-h-[44px] items-center px-2 label-m-semibold text-fg-brand-subtle underline hover:text-fg-brand-strong focus-blue rounded"
        >
          {i18n.get("placesClearFilters")}
        </Link>
      )}
    </div>
  );
}

export default PlacesFilterBar;
